import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FadeInElement } from '@components/animations/FadeInElement/FadeInElement.component';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { CloseIcon } from '@components/icons/Close.icon';
import { MagnifierIcon } from '@components/icons/Magnifier.icon';
import { useTranslation } from '@locales/useTranslation.hook';
import { RootState } from '@store/store';
import { Breakpoint, useIsBreakpointDown, useIsBreakpointUp } from '@styles/breakpoints';
import { PlcColor } from '@styles/colors';
import { usePlcRouter } from '@utils/customHooks';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { GeneralContext } from 'features/business/app.contexts';
import styles from './SearchForm.module.scss';

const MAX_CHARACTERS_SEARCH = 40;

export interface SearchFormProps {
  isOpened: boolean;
  onClose: () => void;
}

const SearchForm: FC<SearchFormProps> = ({ isOpened, onClose }) => {
  const fixedSearchFormRegExp = useMemo(
    () => new RegExp(`^(${urlEnum.search}|${urlEnum.interests}|${urlEnum.activities})/?(.*)`),
    []
  );
  const { t } = useTranslation(['search']);
  const router = usePlcRouter();
  const isFixed = fixedSearchFormRegExp.test(router.pathname);
  const {
    layout: { isMenuOpened },
  } = useSelector((state: RootState) => state);
  const { searchQuery: query, setSearchQuery: setQuery } = useContext(GeneralContext);

  const [searchQuery, setSearchQuery] = useState(query);
  useEffect(() => {
    setSearchQuery(query);
  }, [query]);

  const { route } = usePlcRouter();
  const isJournalPage = route === urlEnum.search;
  const isSmallMobileWindow = useIsBreakpointDown(Breakpoint.xs);
  const isMobile = !useIsBreakpointUp(Breakpoint.l);
  const inputRef = useRef<HTMLInputElement>(null);
  const { push, pathname } = usePlcRouter();

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (inputRef.current) {
      inputRef.current.blur();
    }
    if (setQuery) {
      setQuery(searchQuery || '');
    }
    if (pathname !== urlEnum.search) {
      push(`${urlEnum.search}?mots_cles=${encodeURIComponent(searchQuery || '')}`);
    }
  };

  let rootClass = classnames(
    styles['root-search-form'],
    isFixed ? styles.fixed : '',
    isMenuOpened ? styles['mobile-opened'] : '',
    isOpened ? styles.opened : '',
    'flex'
  );

  if (isJournalPage){
    rootClass = classnames(
      styles['journalPage'],
      isFixed ? styles.fixed : '',
      isMenuOpened ? styles['mobile-opened'] : '',
      isOpened ? styles.opened : '',
      'flex'
    );
  }
  const classes = classnames(
    styles['search-form'],
    isMobile ? styles['mobile-style'] : '',
    isJournalPage ? styles['journal-style'] : ''
  );

  const onInputValueChange = (e: React.FormEvent) => {
    const target = e.target as HTMLInputElement;
    setSearchQuery(target.value);
  };
  let magnifierIconSize;
  if (isJournalPage && isSmallMobileWindow) {
    magnifierIconSize = 20;
  } else {
    magnifierIconSize = isMobile ? 24 : 32;
  }

  useEffect(() => {
    onClose();
  }, [router.pathname]);

  return (
    <div className={rootClass}>
      <div className={classes}>
        <div className={styles['search-form-wrapper']}>
          <div className={styles.spacing} />
          <form onSubmit={submit}>
            <input
              ref={inputRef}
              className="new-p"
              inputMode="search"
              maxLength={MAX_CHARACTERS_SEARCH}
              onChange={onInputValueChange}
              placeholder={t('search.search.placeholder')}
              style={{ fontSize: isJournalPage && isSmallMobileWindow ? '15px' : '' }}
              type="text"
              value={searchQuery}
            />
            <PrimaryButton isDarkGreen type="submit">
              <MagnifierIcon fillColor={PlcColor.GREEN} height={magnifierIconSize} width={magnifierIconSize} />
              <span className="btn-label">{t('search.search')}</span>
            </PrimaryButton>
          </form>
          <div className={styles.spacing}>
            <FadeInElement show={!isFixed}>
              <CloseIcon className="cursor-pointer" fillColor={PlcColor.GREY_CLOUD_DARK} onClick={onClose} width={24} />
            </FadeInElement>
          </div>
        </div>
      </div>
    </div>
  );
};

export { SearchForm };
